<template>
	<div class="gantt">
		<span class="attention" v-if="$vuetify.breakpoint.xs"
			>※ガントチャートはPCもしくはタブレットで使用してください
		</span>
		<v-row>
			<v-col cols="6">
				<slot name="app-bar-btn" />
				<v-spacer></v-spacer>
			</v-col>
			<v-col cols="6" sm="6" md="4">
				<v-menu
					ref="menu"
					v-model="menu"
					:close-on-content-click="false"
					:return-value.sync="start_day"
					transition="scale-transition"
					offset-y
					min-width="auto"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-text-field
							v-model="start_day"
							label="日付を検索"
							prepend-icon="mdi-calendar"
							readonly
							v-bind="attrs"
							v-on="on"
						></v-text-field>
					</template>
					<v-date-picker v-model="start_day" no-title scrollable locale="ja-JP">
						<v-spacer></v-spacer>
						<v-btn text color="primary" @click="menu = false"> 閉じる </v-btn>
						<v-btn
							text
							color="primary"
							@click="
								scrollCalendar()
								$refs.menu.save(start_day)
							"
						>
							検索
						</v-btn>
					</v-date-picker>
				</v-menu>
			</v-col>
		</v-row>

		<div class="gantt-chart">
			<div>
				<div class="gantt-tasks" ref="task" :style="openFlag ? 'height: 8.24rem' : '5rem'">
					<div class="gantt-task">タスク</div>
					<div class="gantt-start">開始予定</div>
					<div class="gantt-end">完了予定</div>
				</div>
				<div :style="`height:${calendarViewHeight}px`">
					<div
						v-for="(task, index) in displayTasks"
						:key="index"
						class="gantt-task-list"
						@dragstart="dragTask(task)"
						@dragover.prevent="dragTaskOver(task)"
						@dragend="dragUpdateTask(task)"
					>
						<template v-if="task.cat === 'category'">
							<slot
								name="category"
								:styleObject="styleObject"
								:task="task"
								:toggleCategory="toggleCategory"
								:addTask="addTask"
							>
								<div draggable="false" class="categories">
									<span :style="styleObject" @click="addTask(task.id)">{{
										task[categoryNameKey]
									}}</span>
									<div class="pr-4" @click="toggleCategory(task.id)">
										<span v-if="task.collapsed">
											<v-icon>mdi-chevron-right</v-icon>
										</span>
										<span v-else>
											<v-icon>mdi-chevron-down</v-icon>
										</span>
									</div>
								</div>
							</slot>
						</template>
						<template v-else>
							<div class="tasks" :draggable="isDraggable">
								<div class="task-name" @click="showDialog(task.id, 1)">
									{{ task[taskNameKey] }}
								</div>
								<div class="task-start" @click="showDialog(task.id, 1)">
									{{ task[startDateKey] | dateFormatter }}
								</div>
								<div class="task-end" @click="showDialog(task.id, 1)">
									{{ task[endDateKey] | dateFormatter }}
								</div>
							</div>
						</template>
					</div>
				</div>
			</div>

			<div class="gantt-calendar" :style="`width:${calendarViewWidth}px`" ref="calendar">
				<div class="gantt-date">
					<div class="gantt-year-month" v-if="openFlag === false">
						<div v-for="(calendar, index) in calendars" :key="index">
							<div
								class="calendar-date"
								:style="`width:${calendar.calendar * block_size}px;left:${
									calendar.start_block_number * block_size
								}px`"
							>
								{{ calendar.date }}
							</div>
						</div>
					</div>

					<div class="gantt-day" v-if="openFlag === false">
						<div v-for="(calendar, index) in calendars" :key="index">
							<div v-for="(day, index) in calendar.days" :key="index">
								<div
									class="gantt-days"
									:class="{
										saturday: day.dayOfWeek === '土',
										sunday: day.dayOfWeek === '日',
										today:
											calendar.year === today.year() &&
											calendar.month === today.month() &&
											day.day === today.date(),
									}"
									:style="`width:${block_size}px;left:${day.block_number * block_size}px`"
								>
									<span>{{ day.day }}</span>
									<span>{{ day.dayOfWeek }}</span>
								</div>
							</div>
						</div>
					</div>
					<div class="gantt-height" v-if="openFlag === false">
						<div v-for="(calendar, index) in calendars" :key="index">
							<div v-for="(day, index) in calendar.days" :key="index">
								<div
									class="days-height"
									:class="{
										saturday: day.dayOfWeek === '土',
										sunday: day.dayOfWeek === '日',
									}"
									:style="`width:${block_size}px;left:${
										day.block_number * block_size
									}px;height:${calendarViewHeight}px`"
								></div>
							</div>
						</div>
					</div>

					<!-- 時間表記エリア -->
					<TimeArea
						:times="times"
						class="gantt-year-month"
						v-if="openFlag"
						:block_size="time_block_size"
						:calendarViewHeight="calendarViewHeight"
						:calendarViewWidth="calendarViewWidth"
						:selectedTask="selectedTask"
						:displayTasks="displayTasks"
						:barStyle="barStyle"
						:changeDatetimeFlag="changeDatetimeFlag"
						:startTimeKey="startTimeKey"
						:startDateKey="startDateKey"
						:endTimeKey="endTimeKey"
						:endDateKey="endDateKey"
						@back="back"
						@change-datetime="sendParent($event)"
					/>
				</div>

				<div
					v-if="openFlag === false"
					class="gantt-bar-area"
					:style="`width:${calendarViewWidth}px;height:${calendarViewHeight}px`"
				>
					<div v-for="(bar, index) in taskBars" :key="index">
						<div
							:style="bar.style"
							v-bind:class="[changeDatetimeFlag ? 'gantt-bar-true' : 'gantt-bar-false']"
							v-if="bar.task.cat === 'task'"
							@mousedown="mouseDownMove(bar.task)"
							@click="openTimeArea(bar)"
						>
							<div
								v-if="changeDatetimeFlag"
								class="gantt-bar-left"
								style="top: 6px; left: -6px; cursor: col-resize"
								@mousedown.stop="mouseDownResize(bar.task, 'left')"
							></div>
							<div
								v-if="changeDatetimeFlag"
								class="gantt-bar-right"
								style="top: 6px; right: -6px; cursor: col-resize"
								@mousedown.stop="mouseDownResize(bar.task, 'right')"
							></div>
						</div>
						<div v-if="bar.task.cat === 'task'" :style="bar.style" class="gantt-bar-task"></div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from "moment/min/moment.min.js"
import TimeArea from "./component/TimeArea.vue"

export default {
	filters: {
		dateFormatter: function (date) {
			if (!date) return ""
			return moment(date).format("YYYY/MM/DD")
		},
	},

	components: { TimeArea },
	props: {
		//draggableの制御
		isDraggable: {
			type: Boolean,
			default: false,
		},
		// trueであればバーの拡大・縮小・移動が可能
		changeDatetimeFlag: {
			type: Boolean,
			default: false,
		},
		// カテゴリーのデータ
		categories: {
			type: Array,
			default: () => [],
		},
		// タスクのデータ
		tasks: {
			type: Array,
			default: () => [],
		},
		// カテゴリーと紐付けされていないタスクのデータ
		notDistributedTasks: {
			type: Array,
			default: () => [],
		},
		// 部門名
		departmentName: {
			type: String,
			default: "",
		},
		// 部門ID
		departmentId: {
			type: Number,
			default: 0,
		},
		categoryNameKey: {
			type: String,
			default: "",
		},
		taskNameKey: {
			type: String,
			default: "",
		},
		startDateKey: {
			type: String,
			default: "",
		},
		endDateKey: {
			type: String,
			default: "",
		},
		startTimeKey: {
			type: String,
			default: "",
		},
		endTimeKey: {
			type: String,
			default: "",
		},
		fontSize: {
			type: String,
			default: "16px",
		},
		isWindowSizeCheck: {
			type: Boolean,
			default: false,
		},
		changeCategoryTaskFlag: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			start_month: moment().add(-10, "years").format("YYYY-MM"),
			end_month: moment().add(10, "years").format("YYYY-MM"),
			block_size: 30,
			time_block_size: 48,
			block_number: 0,
			calendars: [],
			inner_width: "",
			inner_height: "",
			task_width: "",
			task_height: "",
			today: moment(),
			position_id: 0,
			dragging: false,
			pageX: "",
			element: "",
			left: "",
			task_id: "",
			width: "",
			leftResizing: false,
			rightResizing: false,
			task: "",
			dialog: false,
			isValid: false,
			times: [],
			openFlag: false,
			barStyle: null,
			selectedTask: null,
			isResize: false,
			isDragging: false,
			menu: false,
			start_day: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
				.toISOString()
				.substr(0, 10),
		}
	},

	methods: {
		sendParent(e) {
			this.$emit("change-datetime", e)
		},
		back() {
			this.openFlag = false
			this.$nextTick(() => {
				this.todayPosition()
			})
		},
		openTimeArea(bar) {
			// barのリサイズとドラッグ移動の際はopenさせない
			if (this.isResize || this.isDragging) {
				this.openFlag = false
			} else {
				const top = this.extractNumber(bar.style.top)
				bar.style.top = `${top}px`
				bar.style.left = null
				bar.style.width = null
				this.barStyle = bar.style
				this.selectedTask = bar.task
				this.openFlag = true
			}
			this.isResize = false
			this.isDragging = false
		},
		extractNumber(string) {
			const regex = /[^0-9]/g
			const result = string.replace(regex, "")
			const number = parseInt(result)
			return number
		},
		getTimes(block_number) {
			let num = 24
			for (let i = 0; i < num; i++) {
				this.times.push({
					time: i,
					block_number,
				})
				block_number++
			}
		},
		getDays(year, month, block_number) {
			const dayOfWeek = ["日", "月", "火", "水", "木", "金", "土"]
			let days = []
			let date = moment(`${year}-${month}-01`)
			// 今月の日数を取得
			let num = date.daysInMonth()
			for (let i = 0; i < num; i++) {
				days.push({
					day: date.date(),
					dayOfWeek: dayOfWeek[date.day()],
					block_number,
				})
				date.add(1, "day")
				block_number++
			}
			return days
		},
		getCalendar() {
			let block_number = 0
			let days
			let start_month = moment(this.start_month)
			let end_month = moment(this.end_month)
			let between_month = end_month.diff(start_month, "months")
			for (let i = 0; i <= between_month; i++) {
				days = this.getDays(start_month.year(), start_month.format("MM"), block_number)
				this.calendars.push({
					date: start_month.format("YYYY年MM月"),
					year: start_month.year(),
					month: start_month.month(),
					start_block_number: block_number,
					calendar: days.length,
					days: days,
				})
				start_month.add(1, "months")
				block_number = days[days.length - 1].block_number
				block_number++
			}
			return block_number
		},
		getWindowSize() {
			this.inner_width = window.innerWidth
			this.inner_height = window.innerHeight
			this.task_width = this.$refs.task.offsetWidth
			this.task_height = this.$refs.task.offsetHeight
		},
		todayPosition() {
			this.$refs.calendar.scrollLeft = this.scrollDistance
		},
		windowSizeCheck() {
			// TimeAreaのopen時はスキップ
			if (this.openFlag || this.isWindowSizeCheck) return
			let height = this.lists.length - this.position_id
			if (event.deltaY > 0 && height * 40 > this.calendarViewHeight) {
				this.position_id++
			} else if (event.deltaY < 0 && this.position_id !== 0) {
				this.position_id--
			}
		},
		mouseDownMove(task) {
			if (this.changeDatetimeFlag) {
				this.dragging = true
				this.pageX = event.pageX
				this.element = event.target
				this.left = event.target.style.left
				this.task_id = task.id
			}
		},
		mouseMove() {
			if (this.dragging) {
				this.isDragging = true
				let diff = this.pageX - event.pageX
				this.element.style.left = `${parseInt(this.left.replace("px", "")) - diff}px`
			}
		},
		stopDrag() {
			if (this.dragging) {
				// ドラッグ移動で日付更新のパターン
				let diff = this.pageX - event.pageX
				let days = Math.ceil(diff / this.block_size)
				if (days !== 0) {
					let task = this.tasks.find((task) => task.id === this.task_id)
					let start_date = moment(task[this.startDateKey]).add(-days, "days")
					let end_date = moment(
						task[this.endDateKey] ? task[this.endDateKey] : task[this.startDateKey]
					).add(-days, "days")
					console.log(end_date)
					task[this.startDateKey] = start_date.format("YYYY-MM-DD")
					task[this.endDateKey] = end_date.format("YYYY-MM-DD")
					this.$emit("change-datetime", {
						[this.startDateKey]: task[this.startDateKey],
						[this.endDateKey]: task[this.endDateKey],
						id: task.id,
					})
				} else {
					this.element.style.left = `${this.left.replace("px", "")}px`
				}
			}
			if (this.leftResizing) {
				// 開始日更新のパターン
				let diff = this.pageX - event.pageX
				let days = Math.ceil(diff / this.block_size)
				if (days !== 0) {
					let task = this.tasks.find((task) => task.id === this.task_id)
					let start_date = moment(task[this.startDateKey]).add(-days, "days")
					let end_date = moment(
						task[this.endDateKey] ? task[this.endDateKey] : task[this.startDateKey]
					)
					if (end_date.diff(start_date, "days") <= 0) {
						task[this.startDateKey] = end_date.format("YYYY-MM-DD")
					} else {
						task[this.startDateKey] = start_date.format("YYYY-MM-DD")
					}
					this.$emit("change-datetime", {
						[this.startDateKey]: task[this.startDateKey],
						[this.endDateKey]: task[this.endDateKey],
						id: task.id,
					})
				} else {
					this.element.style.width = this.width
					this.element.style.left = `${this.left.replace("px", "")}px`
				}
			}
			if (this.rightResizing) {
				// 終了日更新のパターン
				let diff = this.pageX - event.pageX
				let days = Math.ceil(diff / this.block_size)
				if (days === 1) {
					this.element.style.width = `${parseInt(this.width.replace("px", ""))}px`
				} else if (days <= 2) {
					days--
					let task = this.tasks.find((task) => task.id === this.task_id)
					let end_date = moment(
						task[this.endDateKey] ? task[this.endDateKey] : task[this.startDateKey]
					).add(-days, "days")
					task[this.endDateKey] = end_date.format("YYYY-MM-DD")
					this.$emit("change-datetime", {
						[this.startDateKey]: task[this.startDateKey],
						[this.endDateKey]: task[this.endDateKey],
						id: task.id,
					})
				} else {
					let task = this.tasks.find((task) => task.id === this.task_id)
					let start_date = moment(task[this.startDateKey])
					let end_date = moment(task[this.endDateKey]).add(-days, "days")

					if (end_date.diff(start_date, "days") < 0) {
						task[this.endDateKey] = start_date.format("YYYY-MM-DD")
					} else {
						task[this.endDateKey] = end_date.format("YYYY-MM-DD")
					}
				}
			}
			this.dragging = false
			this.leftResizing = false
			this.rightResizing = false
		},
		mouseDownResize(task, direction) {
			this.isResize = true
			direction === "left" ? (this.leftResizing = true) : (this.rightResizing = true)
			this.pageX = event.pageX
			this.width = event.target.parentElement.style.width
			this.left = event.target.parentElement.style.left
			this.element = event.target.parentElement
			this.task_id = task.id
		},
		mouseResize() {
			if (this.leftResizing) {
				let diff = this.pageX - event.pageX
				if (parseInt(this.width.replace("px", "")) + diff > this.block_size) {
					this.element.style.width = `${parseInt(this.width.replace("px", "")) + diff}px`
					this.element.style.left = `${this.left.replace("px", "") - diff}px`
				}
			}
			if (this.rightResizing) {
				let diff = this.pageX - event.pageX
				if (parseInt(this.width.replace("px", "")) - diff > this.block_size) {
					this.element.style.width = `${parseInt(this.width.replace("px", "")) - diff}px`
				}
			}
		},
		dragTask(dragTask) {
			this.task = dragTask
		},
		dragTaskOver(overTask) {
			let deleteIndex
			let addIndex
			if (this.task.cat !== "category") {
				if (overTask.cat === "category") {
					let updateTask = this.tasks.find((task) => task.id === this.task.id)
					updateTask["category_id"] = overTask["id"]
				} else {
					if (overTask.id !== this.task.id) {
						this.tasks.map((task, index) => {
							if (task.id === this.task.id) deleteIndex = index
						})
						this.tasks.map((task, index) => {
							if (task.id === overTask.id) addIndex = index
						})
						this.tasks.splice(deleteIndex, 1)
						this.task["category_id"] = overTask["category_id"]
						this.tasks.splice(addIndex, 0, this.task)
					}
				}
			}
		},
		dragUpdateTask() {
			if (this.isDraggable) {
				let updateTask = this.tasks.find((task) => task.id === this.task.id)
				this.$emit("update-task", updateTask.id, updateTask.category_id)
			}
		},
		toggleCategory(task_id) {
			let category = this.categories.find((category) => category.id === task_id)
			category["collapsed"] = !category["collapsed"]
		},

		showDialog(id, index) {
			this.$emit("show-task", id, index)
		},
		sendValue(category_value, task_value) {
			this.$emit("distribute-requirement", category_value, task_value)
		},
		sendParent(e) {
			this.$emit("change-datetime", e)
		},
		back() {
			this.openFlag = false
			this.$nextTick(() => {
				this.todayPosition()
			})
		},
		addTask(id) {
			this.$emit("add-task", id)
		},
		scrollCalendar() {
			let start_date = moment(this.start_day)
			let between_days = start_date.diff(this.today, "days")
			let targetDistance = (between_days + 1) * this.block_size

			this.$refs.calendar.scrollLeft = targetDistance + this.scrollDistance
			this.map = false
		},
	},
	computed: {
		cursorStyle() {
			if (!this.isDraggable) return
			return {
				cursor: "pointer",
			}
		},
		calendarViewWidth() {
			return this.inner_width - this.task_width
		},
		calendarViewHeight() {
			return this.inner_height - this.task_height - 48 - 20
		},
		scrollDistance() {
			let start_date = moment(this.start_month)
			let between_days = this.today.diff(start_date, "days")
			return (between_days + 1) * this.block_size - this.calendarViewWidth / 2
		},
		lists() {
			let lists = []
			this.categories.map((category) => {
				lists.push({ cat: "category", ...category })
				this.tasks.map((task) => {
					if (task.category_id === category.id && !category.collapsed) {
						lists.push({ cat: "task", ...task })
					}
				})
			})
			return lists
		},
		taskBars() {
			let start_date = moment(this.start_month)
			let top = 10
			let left
			let between
			let start
			let style
			return this.displayTasks.map((task) => {
				style = {}
				if (task.cat === "task") {
					if (task[this.startDateKey]) {
						let date_from = moment(task[this.startDateKey])
						let date_to = moment(
							task[this.endDateKey] ? task[this.endDateKey] : task[this.startDateKey]
						)

						between = date_to.diff(date_from, "days")
						between++
						start = date_from.diff(start_date, "days")
						left = start * this.block_size
						style = {
							top: `${top}px`,
							left: `${left}px`,
							width: `${this.block_size * between}px`,
						}
					}
				}
				top = top + 40
				return {
					style,
					task,
				}
			})
		},
		displayTasks() {
			let display_task_number = Math.floor(this.calendarViewHeight / 40)
			return this.lists.slice(this.position_id, this.position_id + display_task_number)
		},
		styleObject() {
			return {
				whiteSpace: "pre-wrap",
				fontSize: this.fontSize,
			}
		},
	},
	mounted() {
		this.getCalendar()
		this.getWindowSize()
		this.$nextTick(() => {
			this.todayPosition()
		})
		this.getTimes(0)
		window.addEventListener("resize", this.getWindowSize)
		window.addEventListener("wheel", this.windowSizeCheck)
		window.addEventListener("mousemove", this.mouseMove)
		window.addEventListener("mouseup", this.stopDrag)
		window.addEventListener("mousemove", this.mouseResize)
	},
	watch: {
		tasks(newVal, _) {
			if (!this.selectedTask) return
			const task = newVal.find((t) => t.id == this.selectedTask.id)
			this.selectedTask = task
		},
		deep: true,
	},
}
</script>

<style scoped lang="scss">
@import "../../stylesheet/shared/color.scss";
.attention {
	font-weight: bold;
}
.gantt-chart {
	position: relative;
	display: flex;
	.gantt-tasks {
		height: 5rem;
		display: flex;
		background: $sub-color;
		.gantt-task {
			border-top: solid 1px white;
			border-right: solid 1px white;
			border-bottom: solid 1px white;
			font-weight: bold;
			color: white;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 12rem;
		}
		.gantt-start,
		.gantt-end {
			border-top: solid 1px white;
			border-right: solid 1px white;
			border-bottom: solid 1px white;
			font-weight: bold;
			color: white;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 6rem;
		}
	}
}
.gantt-task-list {
	display: flex;
	height: 40px;
	width: 384px;
	border-bottom: solid 1px #80808045;
}

.categories {
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: rgb(255 228 0 / 20%);
	font-weight: bold;
	padding-left: 10px;
	width: 100%;
	border-right: solid 1px #80808045;
	cursor: pointer;
	span {
		font-size: var(--fontSize);
	}
}

.tasks {
	display: flex;
	box-shadow: 0 0 5px rgb(0 0 0 / 25%);
	border-radius: 5px;
	cursor: pointer;
}

.task-name {
	display: flex;
	border-right: solid 1px #80808045;
	align-items: center;
	padding-left: 16px;
	width: 12rem;
	font-size: 15px;
	justify-content: center;
}
.task-start,
.task-end {
	display: flex;
	border-right: solid 1px #80808045;
	align-items: center;
	width: 6rem;
	font-size: 15px;
	justify-content: center;
}
.gantt-calendar {
	overflow-x: scroll;
	overflow-y: hidden;
	border-left: solid 1px #80808045;
}
.gantt-year-month {
	position: relative;
	height: 2rem;
}
.gantt-date {
	height: 5rem;
}

.calendar-date {
	position: absolute;
	background: $main-color;
	color: white;
	font-weight: bold;
	border-bottom: solid 1px #80808045;
	border-right: solid 1px #80808045;
	border-top: solid 1px #80808045;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 2rem;
}
.gantt-day {
	position: relative;
	height: 3rem;
}
.gantt-days {
	position: absolute;
	border-bottom: solid 1px #80808045;
	border-right: solid 1px #80808045;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: bold;
	height: 3rem;
	font-size: 0.8rem;
	flex-direction: column;
}
.saturday {
	background: #dbeafe;
}
.sunday {
	background: #fee2e2;
}
.today {
	background: #dc2626;
	color: white;
}
.gantt-height {
	position: relative;
}
.days-height {
	position: absolute;
	border-bottom: solid 1px #80808045;
	border-right: solid 1px #80808045;
}
.gantt-bar-area {
	position: relative;
}

.gantt-bar-true {
	position: absolute;
	background: orange;
	height: 20px;
	border-radius: 10px;
	cursor: pointer;
}
.gantt-bar-false {
	position: absolute;
	background: orange;
	height: 20px;
	border-radius: 10px;
}
.gantt-bar-left {
	position: absolute;
	background: #808080a1;
	border: 1px solid black;
	height: 8px;
	width: 8px;
}
.gantt-bar-right {
	position: absolute;
	background: #808080a1;
	border: 1px solid black;
	height: 8px;
	width: 8px;
}
.gantt-bar-task {
	position: absolute;
	background: orange;
	border-radius: 10px;
}
.hoge {
	z-index: 2;
	position: absolute;
	right: 0;
}
</style>
